import React, { useState, useEffect } from 'react';
import { clientsAPI } from '../../services/api';
import ClientModal from './ClientModal';
import {
  Users,
  Plus,
  TestTube,
  Edit2,
  Trash2,
  X,
  Shield,
  ShieldAlert,
  ShieldX,
  ShieldCheck,
  Building,
  User,
  Calendar,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      console.log('🔍 Cargando clientes...');
      console.log('🔑 Token actual:', localStorage.getItem('authToken') ? 'Presente' : 'Ausente');

      const response = await clientsAPI.getAll();
      console.log('📦 Respuesta completa:', response);
      console.log('📊 Datos recibidos:', response.data);
      console.log('📊 Tipo de datos:', typeof response.data);
      console.log('📊 Es array?:', Array.isArray(response.data));

      // Tu API devuelve directamente un array, no un objeto con items
      const clientsData = Array.isArray(response.data) ? response.data : [];
      console.log('👥 Clientes procesados:', clientsData);
      console.log('👥 Cantidad de clientes:', clientsData.length);

      setClients(clientsData);

      if (clientsData.length > 0) {
        console.log('✅ Clientes cargados exitosamente');
        setError(''); // Limpiar errores previos

        // Verificar que el estado se actualizó correctamente
        setTimeout(() => {
          console.log('🔄 Estado de clientes después de setClients:', clientsData.length);
        }, 100);
      } else {
        console.log('⚠️ No se encontraron clientes');
      }
    } catch (error) {
      console.error('❌ Error cargando clientes:', error);
      console.error('📋 Detalles del error:', error.response);
      console.error('📋 Status del error:', error.response?.status);
      console.error('📋 Mensaje del error:', error.response?.data);
      setError('Error al cargar clientes: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const testEndpoints = async () => {
    console.log('🧪 Iniciando prueba de endpoints...');
    const result = await clientsAPI.testEndpoints();
    if (result) {
      console.log('✅ Endpoint que funciona:', result.endpoint);
      setSuccess(`Endpoint encontrado: ${result.endpoint}`);
      // Actualizar los clientes con los datos encontrados
      const clientsData = Array.isArray(result.data) ? result.data : [];
      setClients(clientsData);
    } else {
      setError('No se encontró ningún endpoint que funcione para clientes');
    }
  };

  const handleCreateClient = () => {
    setSelectedClient(null);
    setShowModal(true);
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setShowModal(true);
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este cliente?')) {
      return;
    }

    try {
      await clientsAPI.delete(clientId);
      setSuccess('Cliente eliminado exitosamente');
      loadClients();
    } catch (error) {
      setError('Error al eliminar cliente: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleModalClose = (shouldReload) => {
    setShowModal(false);
    setSelectedClient(null);
    if (shouldReload) {
      loadClients();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getCertificateIcon = (client) => {
    if (client.isCertificateExpired) return <ShieldX className="w-4 h-4" />;
    if (client.isCertificateExpiringSoon) return <ShieldAlert className="w-4 h-4" />;
    if (client.isValidCertificate) return <ShieldCheck className="w-4 h-4" />;
    return <Shield className="w-4 h-4" />;
  };

  const getCertificateText = (client) => {
    if (client.isCertificateExpired) return 'Expirado';
    if (client.isCertificateExpiringSoon) return 'Por vencer';
    if (client.isValidCertificate) return 'Válido';
    return 'Inválido';
  };

  const getCertificateStatus = (client) => {
    if (client.isCertificateExpired) return 'status-inactive';
    if (client.isCertificateExpiringSoon) return 'status-pending';
    if (client.isValidCertificate) return 'status-active';
    return 'status-inactive';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Cargando clientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div className="flex items-center space-x-3 mb-4 sm:mb-0">
            <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Gestión de Clientes
            </h1>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={testEndpoints}
              className="btn-secondary flex items-center space-x-2"
            >
              <TestTube className="w-4 h-4" />
              <span>Probar Endpoints</span>
            </button>
            <button
              onClick={handleCreateClient}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Nuevo Cliente</span>
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-error mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button
              onClick={() => setError('')}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="alert alert-success mb-6">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{success}</span>
            <button
              onClick={() => setSuccess('')}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Estadísticas */}
        {clients.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="card-hover p-4 text-center">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                {clients.length}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Clientes</p>
            </div>

            <div className="card-hover p-4 text-center">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                <ShieldCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
                {clients.filter(c => c.isValidCertificate && !c.isCertificateExpired).length}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Certificados Válidos</p>
            </div>

            <div className="card-hover p-4 text-center">
              <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                <ShieldAlert className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mb-1">
                {clients.filter(c => c.isCertificateExpiringSoon).length}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Por Vencer</p>
            </div>

            <div className="card-hover p-4 text-center">
              <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                <ShieldX className="w-4 h-4 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-1">
                {clients.filter(c => c.isCertificateExpired).length}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Expirados</p>
            </div>

            <div className="card-hover p-4 text-center">
              <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                <User className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
              </div>
              <h3 className="text-2xl font-bold text-cyan-600 dark:text-cyan-400 mb-1">
                {clients.filter(c => c.tipoPersona === 'Fisica').length}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Personas Físicas</p>
            </div>

            <div className="card-hover p-4 text-center">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Building className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-1">
                {clients.filter(c => c.tipoPersona === 'Moral').length}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Personas Morales</p>
            </div>
          </div>
        )}

        {/* Debug Info */}
        <div className="card p-4 mb-6 bg-gray-50 dark:bg-gray-700 border-dashed">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong className="text-gray-900 dark:text-white">🔧 Debug Info:</strong>
            <div className="mt-2 space-y-1">
              <div>Clientes en estado: <span className="font-mono font-medium">{clients.length}</span></div>
              <div>Loading: <span className="font-mono font-medium">{loading.toString()}</span></div>
              <div>Error: <span className="font-mono font-medium">{error ? 'Sí' : 'No'}</span></div>
              {clients.length > 0 && (
                <div>Primer cliente: <span className="font-mono font-medium">{clients[0]?.rfc} - {clients[0]?.razonSocial}</span></div>
              )}
            </div>
          </div>
        </div>

        {/* Tabla de clientes */}
        <div className="card overflow-hidden">
          {clients.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No hay clientes registrados
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                Comienza agregando tu primer cliente
              </p>
              <button
                onClick={handleCreateClient}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear primer cliente
              </button>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">RFC</th>
                    <th className="table-header-cell">Razón Social</th>
                    <th className="table-header-cell">Tipo</th>
                    <th className="table-header-cell">Estado</th>
                    <th className="table-header-cell">Certificado</th>
                    <th className="table-header-cell">Vencimiento</th>
                    <th className="table-header-cell">Fecha Creación</th>
                    <th className="table-header-cell">Acciones</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {clients.map((client) => (
                    <tr key={client.id} className="table-row">
                      <td className="table-cell">
                        <code className="text-sm font-bold bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {client.rfc}
                        </code>
                      </td>
                      <td className="table-cell">
                        <div className="max-w-xs truncate" title={client.razonSocial}>
                          {client.razonSocial}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="status-pending flex items-center space-x-1">
                          {client.tipoPersona === 'Fisica' ? <User className="w-3 h-3" /> : <Building className="w-3 h-3" />}
                          <span>{client.tipoPersonaText}</span>
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className={client.isActive ? 'status-active' : 'status-inactive'}>
                          {client.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className={`${getCertificateStatus(client)} flex items-center space-x-1`}>
                          {getCertificateIcon(client)}
                          <span>{getCertificateText(client)}</span>
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            <span>{formatDate(client.certificateExpirationDate)}</span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            ({client.daysUntilExpiration} días)
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-1 text-sm">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <span>{formatDate(client.createdAt)}</span>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditClient(client)}
                            className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                            title="Editar cliente"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClient(client.id)}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                            title="Eliminar cliente"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <ClientModal
          client={selectedClient}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default ClientManagement;